<script setup>
import {formatDate} from '@shared-utils/date/date_helpers'
import {formatSum} from '@shared-utils/helpers'
import BaseCheckbox from '@shared-components/base/BaseCheckbox.vue'
import BlurPreloaderWrapper from '@shared-components/wrappers/BlurPreloaderWrapper.vue'
import {defineProps, defineModel, defineExpose, ref, computed} from 'vue'
import {useCompanyBillingStore} from '@/views/finance/CompanyBillingStore'
import InvoicePaymentsStatus from '@/views/finance/components/invoice-modal-parts/invoice-payments-section/InvoicePaymentsStatus.vue'

const {getAllQuickBooksPayments} = useCompanyBillingStore()

const payments = defineModel({required: true})

const props = defineProps({
  companyHash: {
    type: String,
    default: '',
    required: true,
  },
  quickBookCustomerId: {
    type: [String, Number],
    default: '',
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  totalPrice: {
    type: Number,
    default: 0,
  },
  showPaymentsStatus: {
    type: Boolean,
    default: true,
  },
  lockedPaymentIds: {
    type: Array,
    default: () => [],
  },
  // When provided, show the invoice's stored figures instead of selection-derived ones.
  paidSum: {
    type: [Number, null],
    default: null,
  },
  balanceDue: {
    type: [Number, null],
    default: null,
  },
})

// Data
const isSelectedAll = ref(false)
const loading = ref(false)
const quickBooksPaymentsList = ref([])
const bookCloseDate = ref(null)

// Computed
// Amount still owed on the invoice (total - already paid); the target selection must cover.
const remainingDue = computed(() => Math.max(Number(props.balanceDue ?? props.totalPrice) || 0, 0))
const totalSelectedPaymentsPrice = computed(() => {
  return payments.value.reduce((sum, item) => {
    return sum + (Number(item.unapplied_amount) || 0)
  }, 0)
})
const paymentsPriceDifference = computed(() => {
  return Math.max(remainingDue.value - totalSelectedPaymentsPrice.value, 0)
})
// Amount actually applied, capped at the remaining balance due.
const appliedPaymentsPrice = computed(() => Math.min(totalSelectedPaymentsPrice.value, remainingDue.value))
// Displayed totals: Payments = selected (applied), Paid = stored paid_sum, Balance Due = stored remaining.
const displayPayments = computed(() => props.paidSum ?? appliedPaymentsPrice.value)
const displayBalanceDue = computed(() => props.balanceDue ?? paymentsPriceDifference.value)
// Balance due covered by the current selection — block selecting more.
const isFullyCovered = computed(() => remainingDue.value > 0 && totalSelectedPaymentsPrice.value >= remainingDue.value)

// Payments already linked to this invoice in QuickBooks — shown checked + disabled, not re-submitted.
const isLocked = payment_id => props.lockedPaymentIds.map(String).includes(String(payment_id))
// Closed-period payments are selectable — the browser extension links them automatically on creation.
const isClosedPeriod = payment => !payment.can_be_auto_applied
const isSelected = payment_id => !!payments.value.find(item => item.id === payment_id)
const hasClosedPeriodPayments = computed(() => quickBooksPaymentsList.value.some(isClosedPeriod))
// Every non-locked payment can be selected; used for the select-all checked state.
const selectablePaymentsCount = computed(() => quickBooksPaymentsList.value.filter(p => !isLocked(p.id)).length)

// Methods
const toggleAllQbPayments = () => {
  if (isSelectedAll.value) {
    payments.value = quickBooksPaymentsList.value
      .filter(p => !isLocked(p.id))
      .map(({id, unapplied_amount}) => ({id, unapplied_amount}))
  } else {
    payments.value = []
  }
}

const togglePaymentInSelected = payment_id => {
  const payment = quickBooksPaymentsList.value.find(p => p.id === payment_id)

  if (isLocked(payment_id)) {
    return
  }

  const exists = payments.value.find(item => item.id === payment_id)

  if (exists) {
    payments.value = payments.value.filter(item => item.id !== payment_id)
  } else if (payment) {
    payments.value.push({
      id: payment.id,
      unapplied_amount: payment.unapplied_amount,
    })
  }
}

const fetchQuickBooksPayments = async () => {
  if (!props.quickBookCustomerId || !props.companyHash) {
    quickBooksPaymentsList.value = []
    return
  }

  loading.value = true

  try {
    const {book_close_date, payments: list} = await getAllQuickBooksPayments(
      props.companyHash,
      props.quickBookCustomerId,
    )
    // Open payments (unapplied balance) are linkable; already-linked ones are kept for display.
    quickBooksPaymentsList.value = (list ?? []).filter(p => Number(p.unapplied_amount) > 0 || isLocked(p.id))
    bookCloseDate.value = book_close_date ?? null
  } finally {
    loading.value = false
  }
}

const resetQbPayments = () => {
  quickBooksPaymentsList.value = []
}

defineExpose({fetchQuickBooksPayments, resetQbPayments, paymentsPriceDifference, totalSelectedPaymentsPrice})
</script>

<template>
  <div>
    <h5 class="custom fs-5 mb-5 fw-semibold d-flex align-items-center gap-2">
      Payments
      <button
        type="button"
        class="btn btn-link p-0"
        :disabled="loading"
        v-tooltip="'Refresh payments'"
        @click="fetchQuickBooksPayments"
      >
        <i class="fa-light fa-arrows-rotate"></i>
      </button>
    </h5>

    <div v-if="hasClosedPeriodPayments" class="alert alert-warning custom mb-4">
      Some payments fall in a closed period<template v-if="bookCloseDate">
        (books closed through <strong>{{ formatDate(bookCloseDate) }}</strong>)</template>.
      They will be linked automatically when the invoice is created via the extension.
    </div>

    <div class="row">
      <div class="col">
        <blur-preloader-wrapper :loading="loading">
        <div class="qb-payments-table">
          <div class="qb-payments-table-head qb-payments-table-row">
            <div class="row">
              <div class="col-auto w-60-px">
                <base-checkbox
                  v-model="isSelectedAll"
                  @change="toggleAllQbPayments"
                  :readonly="props.disabled"
                  :checked="!!selectablePaymentsCount && payments?.length === selectablePaymentsCount"
                  clear-checkbox
                />
              </div>
              <div class="col fw-bold">ID</div>
              <div class="col fw-bold">Date</div>
              <div class="col text-end fw-bold">Payment Amount</div>
              <div class="col text-end fw-bold">Open Amount</div>
            </div>
          </div>
          <div v-if="!quickBooksPaymentsList.length">
            <div class="qb-payments-table-row">
              <div class="text-center">No payments</div>
            </div>
          </div>
          <template v-else>
            <div v-for="(payment, index) in quickBooksPaymentsList" :key="index" class="qb-payments-table-row">
              <div class="row">
                <div class="col-auto w-60-px">
                  <base-checkbox
                    :checked="isSelected(payment.id) || isLocked(payment.id)"
                    :readonly="
                      disabled ||
                      isLocked(payment.id) ||
                      (isFullyCovered && !isSelected(payment.id))
                    "
                    @change="togglePaymentInSelected(payment.id)"
                  />
                </div>
                <div class="col">
                  <a
                    v-if="payment.direct_link"
                    :href="payment.direct_link"
                    target="_blank"
                    v-tooltip="'Open payment in QuickBooks'"
                  >{{ payment.id }}</a>
                  <template v-else>{{ payment.id }}</template>
                  <span
                    v-if="isLocked(payment.id)"
                    class="badge bg-success ms-2"
                    title="Already linked to this invoice in QuickBooks"
                  >linked</span>
                  <span
                    v-else-if="!payment.can_be_auto_applied"
                    class="badge bg-warning text-dark ms-2"
                    title="In a closed period — linked automatically via the extension"
                  >closed period</span>
                </div>
                <div class="col">{{ formatDate(payment.txn_date) }}</div>
                <div class="col text-end">{{ formatSum(payment.total_amount) }}</div>
                <div class="col text-end">{{ formatSum(payment.unapplied_amount) }}</div>
              </div>
            </div>
          </template>
        </div>
        </blur-preloader-wrapper>
        <div class="d-flex justify-content-between align-items-center mt-5">
          <div class="d-flex align-items-center">
            <slot name="link-action" />
          </div>
          <div class="w-230-px custom px-5">
          <div class="d-flex justify-content-between gap-3 mb-3 mt-5 custom fw-semibold">
            <span>Payments</span> <span>{{ formatSum(appliedPaymentsPrice) }}</span>
          </div>

          <div class="d-flex justify-content-between gap-3 mb-3 mt-5 custom fw-semibold">
            <span>Paid</span> <span>{{ formatSum(displayPayments) }}</span>
          </div>

          <div class="d-flex justify-content-between gap-3 mb-3 mt-5 custom fw-semibold">
            <span>Balance Due</span> <span>{{ formatSum(displayBalanceDue) }}</span>
          </div>

          <invoice-payments-status
            v-if="showPaymentsStatus"
            :total-price="totalPrice"
            :total-selected-payments-price="totalSelectedPaymentsPrice"
            :payments-length="payments.length"
            :payments-price-difference="paymentsPriceDifference"
          />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.qb-payments-table {
  display: flex;
  flex-direction: column;
  border-radius: toRem(3);
  border: toRem(0.5) solid #d1d5db;
  background: #f9fafc;
}

.qb-payments-table-head {
  height: toRem(44);
}

.qb-payments-table-row {
  padding: toRem(12) toRem(16);
  border-bottom: toRem(0.5) solid #d1d5db;

  &:last-of-type {
    border-bottom: none;
  }
}

.text-dark-grey {
  color: #4b5563;
}
</style>
